class ProductDescriptionReadMore extends HTMLElement {
  connectedCallback() {
    this.content = this.querySelector('.product-description-read-more__content');
    this.toggle = this.querySelector('.product-description-read-more__toggle');
    this.readMoreText = this.querySelector('[data-read-more-text]');
    this.readLessText = this.querySelector('[data-read-less-text]');

    if (!this.content || !this.toggle) return;

    this.toggle.addEventListener('click', this.onToggle.bind(this));

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.updateOverflowState());
      this.resizeObserver.observe(this.content);
    }

    this.content.querySelectorAll('img').forEach((image) => {
      if (!image.complete) {
        image.addEventListener('load', () => this.updateOverflowState(), { once: true });
      }
    });

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => this.updateOverflowState());
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.updateOverflowState());
    });
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  onToggle() {
    if (!this.hasOverflow()) {
      this.showWithoutToggle();
      return;
    }

    this.setExpanded(!this.classList.contains('is-expanded'));
  }

  setExpanded(isExpanded) {
    this.classList.toggle('is-expanded', isExpanded);
    this.classList.toggle('is-collapsed', !isExpanded);
    this.toggle.hidden = false;
    this.toggle.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
    if (this.readMoreText) this.readMoreText.hidden = isExpanded;
    if (this.readLessText) this.readLessText.hidden = !isExpanded;
  }

  showWithoutToggle() {
    this.classList.remove('is-collapsed');
    this.classList.add('is-expanded');
    this.toggle.hidden = true;
    this.toggle.setAttribute('aria-expanded', 'true');
    if (this.readMoreText) this.readMoreText.hidden = true;
    if (this.readLessText) this.readLessText.hidden = true;
  }

  getNaturalHeight() {
    const clone = this.content.cloneNode(true);
    const width = this.content.offsetWidth;

    clone.style.cssText = [
      'position:absolute',
      'visibility:hidden',
      'pointer-events:none',
      'width:' + width + 'px',
      'height:auto',
      'max-height:none',
      'overflow:visible',
      'display:block',
      '-webkit-line-clamp:unset',
      'line-clamp:unset',
      '-webkit-box-orient:initial'
    ].join(';');

    this.appendChild(clone);
    const height = clone.offsetHeight;
    clone.remove();
    return height;
  }

  getCollapsedLimit() {
    if (this.dataset.method === 'min_height') {
      const maxHeight = parseFloat(
        getComputedStyle(this).getPropertyValue('--product-description-max-height')
      );
      return Number.isFinite(maxHeight) ? maxHeight : null;
    }

    return this.content.clientHeight;
  }

  hasOverflow() {
    if (!this.content.textContent.trim()) {
      return false;
    }

    const naturalHeight = this.getNaturalHeight();
    const collapsedLimit = this.getCollapsedLimit();

    if (collapsedLimit === null) {
      return false;
    }

    return naturalHeight > collapsedLimit + 1;
  }

  updateOverflowState() {
    if (this.classList.contains('is-expanded') && this.toggle.hidden) return;

    if (!this.hasOverflow()) {
      this.showWithoutToggle();
      return;
    }

    if (!this.classList.contains('is-expanded')) {
      this.setExpanded(false);
    }
  }
}

if (!customElements.get('product-description-read-more')) {
  customElements.define('product-description-read-more', ProductDescriptionReadMore);
}
